import React, { useEffect, useState, useCallback } from 'react';
import { useKeycloak } from '@react-keycloak/web';

interface TemplatePreviewData {
  id: string;
  name: string;
  description?: string | null;
  lifecycle_status?: string;
  confidence_score?: string | null;
  estimated_duration_days?: number | null;
  complexity_level?: string | null;
  total_task_count?: number;
  total_form_count?: number;
  total_phase_count?: number;
  phases?: Array<{ name: string; order: number; task_count: number; form_numbers: string[] }>;
}

interface TaskTemplateComparisonModalProps {
  templates: TemplatePreviewData[];
  onClose: () => void;
  onSelect: (templateId: string) => void;
}

function LifecycleBadge({ status }: { status: string }) {
  const config: Record<string, { bg: string; text: string; label: string }> = {
    validated: { bg: 'bg-green-100 dark:bg-green-900/30', text: 'text-green-800 dark:text-green-300', label: 'Validated' },
    draft: { bg: 'bg-yellow-100 dark:bg-yellow-900/30', text: 'text-yellow-800 dark:text-yellow-300', label: 'Draft' },
    stale: { bg: 'bg-red-100 dark:bg-red-900/30', text: 'text-red-800 dark:text-red-300', label: 'Stale' },
  };
  const c = config[status] || config.draft;
  return (
    <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium ${c.bg} ${c.text}`}>
      {c.label}
    </span>
  );
}

const TaskTemplateComparisonModal: React.FC<TaskTemplateComparisonModalProps> = ({
  templates,
  onClose,
  onSelect,
}) => {
  const [closing, setClosing] = useState(false);

  const handleClose = useCallback(() => {
    setClosing(true);
    setTimeout(() => onClose(), 150);
  }, [onClose]);

  // Close on Escape key
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') handleClose();
    };
    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [handleClose]);

  const columns = templates.slice(0, 3); // Max 3 columns

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center">
      {/* Backdrop */}
      <div
        className="absolute inset-0 bg-black bg-opacity-50"
        onClick={handleClose}
      />

      {/* Modal */}
      <div
        className={`relative bg-white dark:bg-gray-800 rounded-xl shadow-2xl max-w-4xl w-full mx-4 max-h-[85vh] overflow-hidden ${closing ? 'lex-modal-exit' : 'lex-modal-enter'}`}
      >
        {/* Header */}
        <div className="flex items-center justify-between px-6 py-4 border-b border-gray-200 dark:border-gray-700">
          <h3 className="text-lg font-semibold text-gray-900 dark:text-white">
            Compare Templates
          </h3>
          <button
            type="button"
            onClick={handleClose}
            className="text-gray-400 hover:text-gray-600 dark:hover:text-gray-300 transition-colors"
          >
            <svg className="h-5 w-5" fill="none" viewBox="0 0 24 24" strokeWidth={2} stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>

        {/* Comparison grid */}
        <div className="overflow-y-auto max-h-[calc(85vh-64px)] p-6">
          <div className={`grid gap-4 ${columns.length === 2 ? 'grid-cols-2' : 'grid-cols-3'}`}>
            {columns.map((tl) => (
              <div
                key={tl.id}
                className="border border-gray-200 dark:border-gray-600 rounded-lg p-4 flex flex-col"
              >
                {/* Template name */}
                <h4 className="text-sm font-semibold text-gray-900 dark:text-white mb-2 truncate">
                  {tl.name}
                </h4>

                {/* Lifecycle badge */}
                <div className="mb-3">
                  <LifecycleBadge status={tl.lifecycle_status || 'draft'} />
                  {tl.confidence_score && (
                    <span className="inline-flex items-center gap-1 ml-2 text-[10px] text-gray-500 dark:text-gray-400">
                      <span className={`inline-block h-2 w-2 rounded-full ${
                        tl.confidence_score === 'high' ? 'bg-green-500' :
                        tl.confidence_score === 'medium' ? 'bg-amber-500' : 'bg-red-500'
                      }`} />
                      {tl.confidence_score}
                    </span>
                  )}
                </div>

                {/* Stats */}
                <div className="grid grid-cols-3 gap-2 mb-3">
                  <div className="bg-gray-50 dark:bg-gray-700/50 rounded p-2 text-center">
                    <div className="text-lg font-bold text-gray-900 dark:text-white">{tl.total_phase_count || 0}</div>
                    <div className="text-[10px] text-gray-500 dark:text-gray-400">Phases</div>
                  </div>
                  <div className="bg-gray-50 dark:bg-gray-700/50 rounded p-2 text-center">
                    <div className="text-lg font-bold text-gray-900 dark:text-white">{tl.total_task_count || 0}</div>
                    <div className="text-[10px] text-gray-500 dark:text-gray-400">Tasks</div>
                  </div>
                  <div className="bg-gray-50 dark:bg-gray-700/50 rounded p-2 text-center">
                    <div className="text-lg font-bold text-gray-900 dark:text-white">{tl.total_form_count || 0}</div>
                    <div className="text-[10px] text-gray-500 dark:text-gray-400">Forms</div>
                  </div>
                </div>

                {/* Complexity & Duration */}
                <div className="space-y-1 mb-3 text-xs text-gray-600 dark:text-gray-400">
                  {tl.complexity_level && (
                    <div className="flex justify-between">
                      <span>Complexity</span>
                      <span className="font-medium capitalize">{tl.complexity_level}</span>
                    </div>
                  )}
                  {tl.estimated_duration_days && (
                    <div className="flex justify-between">
                      <span>Est. Duration</span>
                      <span className="font-medium">{tl.estimated_duration_days} days</span>
                    </div>
                  )}
                </div>

                {/* Phase list */}
                {tl.phases && tl.phases.length > 0 && (
                  <div className="flex-1 mb-3">
                    <p className="text-[10px] font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wide mb-1">Phases</p>
                    <div className="space-y-1">
                      {tl.phases.sort((a, b) => a.order - b.order).map((phase, idx) => (
                        <div key={idx} className="flex items-center justify-between text-xs">
                          <span className="text-gray-700 dark:text-gray-300 truncate">{phase.order}. {phase.name}</span>
                          <span className="text-gray-400 dark:text-gray-500 shrink-0 ml-1">{phase.task_count}t</span>
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {/* Select button */}
                <button
                  type="button"
                  onClick={() => { onSelect(tl.id); handleClose(); }}
                  className="mt-auto w-full py-2 px-3 text-sm font-medium text-white bg-blue-600 hover:bg-blue-700 rounded-lg transition-colors"
                >
                  Select
                </button>
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
};

export default TaskTemplateComparisonModal;
